import React, { useState, useEffect } from 'react'
import { Input, Tag, Space, Button } from '@haier/bwd'

const PROJECT_SCODE_STORAGE_KEY = 'project_scode'
const PROJECT_SCODE_HISTORY_KEY = 'project_scode_history'
const MAX_HISTORY_COUNT = 10

function loadStoredSCode(): string {
  try {
    return localStorage.getItem(PROJECT_SCODE_STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

function loadSCodeHistory(): string[] {
  try {
    const raw = localStorage.getItem(PROJECT_SCODE_HISTORY_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveSCode(value: string) {
  try {
    localStorage.setItem(PROJECT_SCODE_STORAGE_KEY, value)
  } catch (e) {
    console.error('Failed to save project S code:', e)
  }
}

function saveSCodeHistory(history: string[]) {
  try {
    localStorage.setItem(PROJECT_SCODE_HISTORY_KEY, JSON.stringify(history))
  } catch (e) {
    console.error('Failed to save project S code history:', e)
  }
}

export interface ProjectSCodeModalProps {
  open: boolean
  onConfirm: (scode: string) => void
  /** 确认按钮加载状态（如登录中） */
  confirmLoading?: boolean
}

const pageStyle: React.CSSProperties = {
  width: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f5f5f5',
  padding: 24,
  boxSizing: 'border-box',
}

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  padding: 32,
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}

const titleStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 20,
  fontWeight: 600,
  color: '#262626',
  textAlign: 'center',
}

const ProjectSCodeModal: React.FC<ProjectSCodeModalProps> = ({
  open,
  onConfirm,
  confirmLoading = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [history, setHistory] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setHistory(loadSCodeHistory())
      const lastUsed = loadStoredSCode()
      if (lastUsed) setInputValue(lastUsed)
    }
  }, [open])

  const handleConfirm = () => {
    const scode = inputValue.trim()
    if (!scode) return

    saveSCode(scode)

    const newHistory = [scode, ...history.filter((h) => h !== scode)].slice(0, MAX_HISTORY_COUNT)
    setHistory(newHistory)
    saveSCodeHistory(newHistory)

    onConfirm(scode)
    setInputValue('')
  }

  const handleTagClick = (scode: string) => {
    setInputValue(scode)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  if (!open) return null

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>输入项目 S 码</h1>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input
            placeholder="请输入项目 S 码"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            allowClear
          />
          {history.length > 0 && (
            <div>
              <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.65)', fontSize: 12 }}>
                之前填入的 S 码（点击可选中）：
              </div>
              <Space wrap size={[8, 8]}>
                {history.map((scode) => (
                  <Tag
                    key={scode}
                    style={{ cursor: 'pointer' }}
                    color={inputValue === scode ? 'blue' : 'default'}
                    onClick={() => handleTagClick(scode)}
                  >
                    {scode}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={confirmLoading}
            disabled={!inputValue.trim()}
            block
          >
            确定
          </Button>
        </Space>
      </div>
    </div>
  )
}

export default ProjectSCodeModal
export { loadStoredSCode, PROJECT_SCODE_STORAGE_KEY, PROJECT_SCODE_HISTORY_KEY }
